import React, {useState} from 'react';
import {Pressable, View} from 'react-native';
import Ionicons from 'react-native-vector-icons/Ionicons';

import CCText from '../../../Atoms/CCText';
import type {CourseCover as Cover} from '../../../Hooks/LmsCourseApi/types';
import CourseCover from '../components/CourseCover';
import {COVER_H, COVER_W} from '../coverLogic';
import {E} from './editorTheme';
import {focusVisible, ring} from './ui';

type Props = {
  cover?: Cover | null;
  /** The AI artwork: shown alone until the cover has loaded. */
  artwork?: string | null;
  onPress: () => void;
  /** A smaller tile for a narrow header. */
  compact?: boolean;
};

/**
 * The course's thumbnail, small, as the way into its editor — the picture
 * says what it edits, where a bare image icon did not. The pencil is always
 * on it, since touch has no hover; a hover adds "Edit" over the picture.
 */
export default function ThumbnailButton({cover, artwork, onPress, compact}: Props) {
  const [focused, setFocused] = useState(false);
  const height = compact ? 32 : 40;
  const width = Math.round((height * COVER_W) / COVER_H);
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel="Edit thumbnail"
      onPress={onPress}
      onFocus={() => setFocused(focusVisible())}
      onBlur={() => setFocused(false)}
      style={[{width, height, cursor: 'pointer'} as any, ring(focused)]}>
      {({hovered, pressed}: any) => (
        <>
          <View
            style={{
              width,
              height,
              borderRadius: 6,
              overflow: 'hidden',
              borderWidth: 1,
              borderColor: hovered ? E.accent : E.line,
              backgroundColor: '#14100D',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
            {cover ? (
              <CourseCover cover={cover} thumbnail={artwork} width={width} />
            ) : artwork ? (
              <img
                src={artwork}
                alt=""
                style={{width: '100%', height: '100%', objectFit: 'cover'}}
              />
            ) : (
              <Ionicons name="image-outline" size={18} color="#FFFFFF" />
            )}
            {hovered || pressed ? (
              <View
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: 'rgba(0,0,0,0.55)',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                <CCText style={{fontSize: 12, fontWeight: '700', color: '#FFFFFF'}}>Edit</CCText>
              </View>
            ) : null}
          </View>
          <View
            style={{
              position: 'absolute',
              right: -5,
              bottom: -5,
              width: 18,
              height: 18,
              borderRadius: 9,
              backgroundColor: E.accent,
              borderWidth: 2,
              borderColor: E.surface,
              alignItems: 'center',
              justifyContent: 'center',
            }}>
            <Ionicons name="pencil" size={9} color="#FFFFFF" />
          </View>
        </>
      )}
    </Pressable>
  );
}
